import { UserType, type User } from '../../types/api';
import { LEGACY_DASHBOARD, STUDIO_BASE, STUDIO_BILLING, STUDIO_HOME } from '../../routes/paths';
import { resolveSuspendedAccount } from '../dashboard/use-suspended-account';

/**
 * The "client" user types — legacy's `InList(nUserTypeID, Array("4","5","6"))`, which is how
 * `inc/begin.asp` spells the client side of the app (e.g. `:487`, Bulk Payment). Group Admin,
 * Client and Assistant.
 *
 * Listed explicitly rather than as "not Admin/Owner/TeamMember" so that a user type added later is
 * NOT silently pushed onto legacy.
 */
const CLIENT_USER_TYPES: readonly UserType[] = [
    UserType.GroupAdmin,
    UserType.Client,
    UserType.Assistant
];

/**
 * Whether sign-in should hand this user to the Classic ASP app instead of the React studio.
 *
 * TEMPORARY, AND ONLY FOR SIGN-IN (user decision, 2026-09-21). The client-facing pages are not
 * migrated yet, so a client who signs in from outside — the login form, Google SSO, a password
 * reset, a registration confirmation — belongs on the legacy dashboard for now. Deleting this
 * function and its call below is the whole of the revert when those pages are ready.
 *
 * ★ NOT A PERMISSION CHECK, and not a gate on the React app. A client who reaches a `/ux/…` URL
 * any other way still gets it: this only decides where the sign-in redirect points. In particular
 * "Access Account" and "Direct Login" into a client account keep landing in the studio
 * (`DEFAULT_ACCESS_ACCOUNT_LANDING`, `hooks/use-impersonation.ts`) — they never come through here.
 *
 * An unknown user (no session yet, or an API that did not send `userType`) is NOT treated as a
 * client: the studio home stays the default, as before.
 */
function belongsOnLegacy(user: Pick<User, 'userType'> | undefined): boolean {
    return user?.userType != null && CLIENT_USER_TYPES.includes(user.userType);
}

/**
 * UX-833 AC4. A studio URL the login form was shown on, to return to after sign-in.
 *
 * Only a path under `STUDIO_BASE` is accepted, and it is compared as a whole path segment so
 * `/ux/studioXYZ` cannot pass. The value is built from `window.location` by the studio shell, so
 * this is a guard against a mistake rather than against an attacker — but it is also what keeps a
 * `//host` pathname (legal: `https://site//x` has pathname `//x`) from turning a same-document
 * return into a protocol-relative navigation off-origin.
 */
function studioReturnPath(returnTo: string | undefined): string | undefined {
    if (!returnTo) return undefined;
    return returnTo === STUDIO_BASE || returnTo.startsWith(`${STUDIO_BASE}/`)
        ? returnTo
        : undefined;
}

/**
 * ROBO-478. Where a user goes the moment they sign in.
 *
 * The Owner of a suspended business lands on billing (`STUDIO_BILLING`, React's counterpart of
 * legacy `PAYMENTS_SCREEN`); everyone else on `returnTo` when the form was shown on a studio URL
 * (UX-833 AC4 — a deep link opened signed out, or a session that expired mid-work, signs back in
 * where they were), and on the studio home otherwise, as before. Legacy parity:
 * `Login/login.asp:75-77`.
 *
 * Suspension outranks `returnTo` deliberately: legacy sends that Owner to payments from wherever
 * they signed in, and the studio page they were heading for would only bounce them anyway.
 *
 * A CLIENT USER TYPE (4/5/6) LANDS ON THE LEGACY DASHBOARD instead — see {@link belongsOnLegacy}.
 * That also outranks `returnTo`, and for the same reason: if clients are not to be handed the
 * studio at sign-in, a studio URL they happened to open signed out is not an exception. Suspension
 * is still checked first, but the two cannot collide in practice — `resolveSuspendedAccount` only
 * answers `payments` for an Owner.
 *
 * SIGN-IN ONLY. Call it from the login form and from the hub login page's authorize hand-off (the
 * Google return, or a visitor who already holds a session) — never from a token refresh or the
 * studio layout's session restore. Legacy has no per-page gate, so an Owner refreshed mid-work with
 * `suspended: true` stays where they are.
 */
export function postLoginDestination(
    user: Pick<User, 'userType' | 'suspended'> | undefined,
    returnTo?: string
): string {
    if (resolveSuspendedAccount(user).landingPage === 'payments') return STUDIO_BILLING;
    if (belongsOnLegacy(user)) return LEGACY_DASHBOARD;
    return studioReturnPath(returnTo) ?? STUDIO_HOME;
}
